/**
 * PR-K follow-up — framework-agnostic Stage Camera API.
 *
 * # Purpose
 *
 * The legacy SVG `PrimitiveAtlasStage` carried ~1700 LOC of camera
 * math (zoomExtents, zoomWindow, zoomAll, zoomPrevious, zoomObject)
 * that was wired through `stageCameraApiRef` so the command line and
 * ribbon could invoke ZOOM commands. PR-K replaced that stage with the
 * Three.js `Scene3DStage`, which currently `void`s
 * `props.stageCameraApiRef` instead of populating it — every ZOOM
 * command hits a `null` reference.
 *
 * This module provides the new implementation. It is intentionally
 * **framework-agnostic**: pure math + state mutation through ref-style
 * bindings. No imports from React, no imports from Three.js. The R3F
 * wiring (camera tracker, tween driver, OrthographicCamera updates)
 * lives in `Scene3DStage.tsx` — that side passes `cameraStateRef`,
 * `viewportSizeRef`, the bounds providers and (optionally) a tween
 * callback into `createStageCameraApi`.
 *
 * # AutoCAD parity (ZOOM padding policy)
 *
 *   - **Extents / All**: 10% padding (AutoCAD ZE applies a 0.9× zoom
 *     factor relative to a tight fit — the ezdxf reference and the old
 *     PrimitiveAtlasStage both use 0.9).
 *   - **Object**: 20% padding (ZOOM Object emphasises the picked set —
 *     extra breathing room so the user does not see the entity
 *     touching the viewport edges).
 *   - **Window**: 0% padding. The user explicitly draws the rectangle
 *     they want filled; AutoCAD honours that exactly.
 *
 * # Coordinate convention
 *
 *   - `cameraStateRef.current` holds **world-space** centre coords and
 *     a `zoom` factor expressed as **CSS pixels per world unit**.
 *   - "World-space" here means the RENDERER's OFFSET-world frame: the DXF/WCS
 *     coordinate minus the locked per-session `scene3d/coordinateOrigin`
 *     (every geometry adapter subtracts it for Float32 precision, and the live
 *     ortho this module drives lives in it). This module is deliberately
 *     dependency-free and does NO frame conversion — callers hand it
 *     offset-world points. The one entry that receives DOCUMENT coordinates,
 *     the typed `ZW x1,y1 x2,y2` form, is translated by
 *     `cameraApiAdapter.zoomWindowExact` before it gets here.
 *   - Bounds providers return offset-world axis-aligned rectangles (the
 *     spatial index they read is offset-world since commit 1489a97).
 *   - The R3F-side tracker is responsible for translating
 *     `CameraStateSnapshot` into Three.js OrthographicCamera frustum
 *     parameters — this module never touches THREE.
 *
 * # History stack
 *
 * ZOOM Previous walks back through a 32-entry ring buffer of camera
 * states. Callers (Scene3DStage) push the *current* state onto the
 * stack via `pushHistory(...)` immediately before any zoom mutation,
 * so `zoomPrevious` always restores the snapshot taken before the
 * most recent change. The 32-entry cap matches AutoCAD's documented
 * ZOOM Previous depth (Help → ZOOM command).
 */

/** Snapshot of the camera state needed to drive the THREE viewport. */
export interface CameraStateSnapshot {
  /** World-space X coordinate at the centre of the viewport. */
  centerX: number;
  /** World-space Y coordinate at the centre of the viewport. */
  centerY: number;
  /** Zoom factor — CSS pixels per world unit (must be > 0). */
  zoom: number;
}

/** Axis-aligned bounding rectangle in world space. */
export interface ZoomTargetBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** Options accepted by `setCamera`. */
export interface SetCameraOptions {
  /** Animate via the tween callback if one was supplied. */
  animate?: boolean;
}

/**
 * Public API consumed by the command line, ribbon, and Scene3DStage.
 * Behaviour mirrors the legacy SVG-stage implementation modulo the
 * coord-system change (CAD Y-up vs SVG Y-down) handled inside the R3F
 * wiring layer.
 */
export interface StageCameraApi {
  /** Read the current camera state (shallow copy — safe to retain). */
  getCamera(): CameraStateSnapshot;
  /** Apply a new camera state (immediate, or tweened if animate+tween). */
  setCamera(state: CameraStateSnapshot, options?: SetCameraOptions): void;
  /**
   * #293 (FU-TELE) — every zoom op returns the camera target it COMMITTED
   * (wrote synchronously, or scheduled via `setCameraTween`), `null` when the
   * op was a no-op (missing bounds, empty history, 0×0 viewport). Callers MUST
   * use this returned target — never a before/after diff of `getCamera()` —
   * to decide "did the view change": with a tween binding the live
   * `cameraStateRef` is mutated on LATER animation frames, so right after the
   * call `getCamera()` still reports the pre-zoom state and a synchronous diff
   * always (wrongly) reads "unchanged". The returned target is the
   * authoritative end-state of the op for both the sync and the tween path.
   */
  /** ZOOM Extents — fit all entities with 10% padding. */
  zoomExtents(): CameraStateSnapshot | null;
  /** ZOOM All — same target as Extents while we lack a separate "limits" rect. */
  zoomAll(): CameraStateSnapshot | null;
  /**
   * ZOOM Window — fit the user-defined rectangle exactly (no padding).
   * Corners are in the RENDERER (offset-world) frame, like every other
   * coordinate on this surface — see "Coordinate convention" above. The
   * document→offset translation for the typed `ZW x1,y1 x2,y2` literals
   * happens one level up, in `cameraApiAdapter.zoomWindowExact`.
   */
  zoomWindow(
    corner1: { x: number; y: number },
    corner2: { x: number; y: number },
  ): CameraStateSnapshot | null;
  /** ZOOM Object — fit the picked entity set with 20% padding. */
  zoomObject(entityIds: string[]): CameraStateSnapshot | null;
  /** ZOOM Previous — pop the last pushed snapshot off the history stack. */
  zoomPrevious(): CameraStateSnapshot | null;
  /**
   * PAN by an explicit WORLD-space displacement (the `-PAN x1,y1 x2,y2`
   * two-point form: `dx = x2 - x1`, `dy = y2 - y1`). The DRAWING moves by
   * `+(dxWorld, dyWorld)` — AutoCAD's `-PAN` "first point moves TO the second"
   * semantics — which requires translating the camera CENTRE by
   * `−(dxWorld, dyWorld)` (a camera that moves left makes the drawing appear to
   * move right). Returns the COMMITTED target — or `null` on a zero
   * displacement (no-op). Like `applyFit`, the caller MUST gate telemetry on
   * this returned target rather than diffing `getCamera()`: the commit is
   * tween-deferred so the live `cameraStateRef` still reads the pre-pan centre
   * right after the call.
   *
   * The commit goes through `setCamera(..., { animate })` (the same
   * `pendingTweenRef` path the ZOOM fits use): a plain `cameraStateRef` write
   * would be CLOBBERED next frame by `CameraApiBinder`, which mirrors the live
   * ortho `position` back into the ref every frame — only moving the actual
   * THREE camera (which the tween driver does) makes a displacement pan stick.
   *
   * cmd-pan P2-2 — the displacement is applied onto the PENDING tween target
   * when a tween is still in flight (via `getPendingTweenTarget`), so
   * back-to-back scripted pans accumulate (`−(d1 + d2)`) instead of the second
   * overwriting the first from the stale pre-pan centre.
   */
  pan(
    dxWorld: number,
    dyWorld: number,
    options?: SetCameraOptions,
  ): CameraStateSnapshot | null;
  /** Push the supplied state onto the history ring buffer (cap 32). */
  pushHistory(state: CameraStateSnapshot): void;
}

/**
 * Bindings — the R3F-side glue that backs the framework-agnostic API
 * with live camera state, viewport metrics and entity-bounds queries.
 */
export interface CameraApiBindings {
  /** Mutable camera state shared with the R3F OrthographicCamera tracker. */
  cameraStateRef: { current: CameraStateSnapshot };
  /** CSS pixel size of the canvas (drives the fit math). */
  viewportSizeRef: { current: { width: number; height: number } };
  /** Combined model + paper-space bounding rectangle, or `null` if empty. */
  getAllEntitiesBounds: () => ZoomTargetBounds | null;
  /** Bounding rectangle for a specific id set, or `null` if empty. */
  getEntitiesBounds: (ids: string[]) => ZoomTargetBounds | null;
  /**
   * Optional per-frame tween driver. If absent, `setCamera` writes the
   * target state straight into `cameraStateRef.current`.
   */
  setCameraTween?: (target: CameraStateSnapshot, durationMs: number) => void;
  /**
   * cmd-pan P2-2 — the END-STATE target of an IN-FLIGHT tween, or `null` when
   * no tween is pending. `pan()` reads this so two `-PAN` displacements issued
   * back-to-back (before the 300ms tween advances `cameraStateRef`) ACCUMULATE
   * onto the pending target instead of both reading the stale pre-pan centre
   * (the second would otherwise overwrite the first, collapsing repeated
   * scripted pans). Absent in non-tweened harnesses (then `pan()` falls back to
   * the live `cameraStateRef`, the immediate-write path).
   */
  getPendingTweenTarget?: () => CameraStateSnapshot | null;
}

/** Hard cap on the ZOOM Previous history depth (matches AutoCAD). */
export const CAMERA_HISTORY_LIMIT = 32;

/** Default tween duration in ms when `animate: true` is requested. */
export const CAMERA_TWEEN_DURATION_MS = 300;

/** AutoCAD ZE / ZA padding factor — 0.9× tight fit ⇒ 10% margin. */
const EXTENTS_PADDING_FACTOR = 0.9;

/** ZOOM Object — 0.8× tight fit ⇒ 20% margin (more breathing room). */
const OBJECT_PADDING_FACTOR = 0.8;

/** ZOOM Window — exact fit, no padding. */
const WINDOW_PADDING_FACTOR = 1.0;

/**
 * Fallback zoom used when bounds are degenerate (single point, empty
 * drawing, etc.). Mirrors the old SVG-stage `defaultZoom`.
 */
const FALLBACK_ZOOM = 1;

/**
 * Compute the zoom-and-centre that fits `bounds` into the viewport
 * with the given padding factor. Returns `null` if the viewport is
 * 0×0 (caller should bail without mutating state).
 */
function fitBoundsToViewport(
  bounds: ZoomTargetBounds,
  viewport: { width: number; height: number },
  paddingFactor: number,
): CameraStateSnapshot | null {
  if (viewport.width <= 0 || viewport.height <= 0) {
    return null;
  }
  const bboxWidth = bounds.maxX - bounds.minX;
  const bboxHeight = bounds.maxY - bounds.minY;
  const centerX = (bounds.minX + bounds.maxX) / 2;
  const centerY = (bounds.minY + bounds.maxY) / 2;

  // Degenerate single point — keep the centre but fall back to a
  // sensible default zoom (AutoCAD ZE on a single dot also keeps the
  // viewport at user-default scale rather than zooming to infinity).
  if (bboxWidth <= 0 && bboxHeight <= 0) {
    return { centerX, centerY, zoom: FALLBACK_ZOOM };
  }

  // Fit each axis independently; pick the tighter constraint so the
  // whole bbox fits. A zero on one axis (vertical/horizontal line) is
  // ignored — `Number.POSITIVE_INFINITY` so the other axis wins.
  const zoomX = bboxWidth > 0 ? viewport.width / bboxWidth : Number.POSITIVE_INFINITY;
  const zoomY = bboxHeight > 0 ? viewport.height / bboxHeight : Number.POSITIVE_INFINITY;
  const tightZoom = Math.min(zoomX, zoomY);

  // Both axes degenerate (already handled above) or both infinite —
  // unreachable in practice but guard against NaN/∞ leakage.
  if (!Number.isFinite(tightZoom) || tightZoom <= 0) {
    return { centerX, centerY, zoom: FALLBACK_ZOOM };
  }

  return {
    centerX,
    centerY,
    zoom: tightZoom * paddingFactor,
  };
}

/** Construct a new StageCameraApi backed by the supplied bindings. */
export function createStageCameraApi(
  bindings: CameraApiBindings,
): StageCameraApi {
  const history: CameraStateSnapshot[] = [];

  function getCamera(): CameraStateSnapshot {
    const cur = bindings.cameraStateRef.current;
    return { centerX: cur.centerX, centerY: cur.centerY, zoom: cur.zoom };
  }

  function setCamera(
    state: CameraStateSnapshot,
    options?: SetCameraOptions,
  ): void {
    const target: CameraStateSnapshot = {
      centerX: state.centerX,
      centerY: state.centerY,
      zoom: state.zoom,
    };
    if (options?.animate === true && bindings.setCameraTween !== undefined) {
      bindings.setCameraTween(target, CAMERA_TWEEN_DURATION_MS);
      return;
    }
    bindings.cameraStateRef.current = target;
  }

  function pushHistory(state: CameraStateSnapshot): void {
    history.push({
      centerX: state.centerX,
      centerY: state.centerY,
      zoom: state.zoom,
    });
    // Ring-buffer trim — drop oldest entries past the cap so the stack
    // never grows unbounded during a long editing session.
    while (history.length > CAMERA_HISTORY_LIMIT) {
      history.shift();
    }
  }

  /**
   * #293 (FU-TELE) — returns the target it committed via `setCamera` (`null`
   * on the 0×0-viewport bail) so callers learn the end-state of the fit even
   * when the actual `cameraStateRef` write is deferred to tween frames.
   */
  function applyFit(
    bounds: ZoomTargetBounds | null,
    paddingFactor: number,
    options?: SetCameraOptions,
  ): CameraStateSnapshot | null {
    const viewport = bindings.viewportSizeRef.current;
    if (bounds === null) {
      // Empty drawing — preserve current centre, reset to default zoom.
      // Matches AutoCAD ZE on an empty model: no crash, view stays put.
      const fallback: CameraStateSnapshot = {
        centerX: 0,
        centerY: 0,
        zoom: FALLBACK_ZOOM,
      };
      setCamera(fallback, options);
      return fallback;
    }
    const fitted = fitBoundsToViewport(bounds, viewport, paddingFactor);
    if (fitted === null) {
      // 0×0 viewport — no-op, do not poison the camera with NaN.
      return null;
    }
    setCamera(fitted, options);
    return fitted;
  }

  function zoomExtents(): CameraStateSnapshot | null {
    const bounds = bindings.getAllEntitiesBounds();
    return applyFit(bounds, EXTENTS_PADDING_FACTOR, { animate: true });
  }

  function zoomAll(): CameraStateSnapshot | null {
    // For now ZA == ZE because the model has no separate "limits"
    // rectangle yet (the legacy SVG stage made the same simplification
    // and AutoCAD's documented behaviour collapses to ZE when no
    // limits are set / the drawing extends past them).
    const bounds = bindings.getAllEntitiesBounds();
    return applyFit(bounds, EXTENTS_PADDING_FACTOR, { animate: true });
  }

  function zoomWindow(
    corner1: { x: number; y: number },
    corner2: { x: number; y: number },
  ): CameraStateSnapshot | null {
    const bounds: ZoomTargetBounds = {
      minX: Math.min(corner1.x, corner2.x),
      minY: Math.min(corner1.y, corner2.y),
      maxX: Math.max(corner1.x, corner2.x),
      maxY: Math.max(corner1.y, corner2.y),
    };
    return applyFit(bounds, WINDOW_PADDING_FACTOR, { animate: true });
  }

  function zoomObject(entityIds: string[]): CameraStateSnapshot | null {
    if (entityIds.length === 0) {
      return null;
    }
    const bounds = bindings.getEntitiesBounds(entityIds);
    if (bounds === null) {
      return null;
    }
    return applyFit(bounds, OBJECT_PADDING_FACTOR, { animate: true });
  }

  function zoomPrevious(): CameraStateSnapshot | null {
    const previous = history.pop();
    if (previous === undefined) {
      return null;
    }
    setCamera(previous, { animate: true });
    return previous;
  }

  /**
   * PAN by a world-space displacement (zoom unchanged). Returns the committed
   * target so the caller gates telemetry on a real delta — `null` on a
   * zero-displacement no-op (mirrors `applyFit`'s honest-return contract so a
   * `-PAN p,p` that moves nothing records no view-op, #269/#R4).
   *
   * #R1 (THE CRUX) — the commit goes through `setCamera(..., { animate: true })`,
   * the SAME tween/`pendingTweenRef` path EVERY zoom fit uses (`applyFit` /
   * `zoomPrevious` all hardcode `{ animate: true }`). This is load-bearing: a
   * plain `cameraStateRef` write (the `animate:false` branch of `setCamera`)
   * would be CLOBBERED next frame by `CameraApiBinder`, which mirrors the live
   * ortho `position` BACK into `cameraStateRef` every frame — only the tween
   * driver moves the actual THREE ortho, so only the tween path makes a
   * displacement pan visibly stick. `options` is accepted for symmetry but the
   * commit always animates (matching the zoom ops, whose `_opts` is likewise
   * advisory — the inner op owns the animate decision).
   *
   * cmd-pan P2-1 — the camera centre moves by `−(dxWorld, dyWorld)`: AutoCAD
   * `-PAN`'s displacement moves the DRAWING by `+(dx, dy)` (first point → second
   * point), and a drawing that shifts right is produced by a camera that shifts
   * left. cmd-pan P2-2 — the base for the new target is the PENDING tween's
   * end-state (when a tween is mid-flight) rather than the live `cameraStateRef`
   * (which still reads the pre-pan centre under a tween), so consecutive scripted
   * pans accumulate.
   */
  function pan(
    dxWorld: number,
    dyWorld: number,
    _options?: SetCameraOptions,
  ): CameraStateSnapshot | null {
    if (dxWorld === 0 && dyWorld === 0) {
      // Zero displacement — moved nothing; no commit, no view-op (#R4).
      return null;
    }
    // P2-2 — accumulate onto the in-flight tween target so back-to-back pans
    // chain. Falls back to the live ref when no tween is pending (the first pan,
    // or the non-tweened immediate-write harness).
    const base =
      bindings.getPendingTweenTarget?.() ?? bindings.cameraStateRef.current;
    const target: CameraStateSnapshot = {
      // P2-1 — NEGATE: drawing moves +delta ⇒ camera centre moves −delta.
      centerX: base.centerX - dxWorld,
      centerY: base.centerY - dyWorld,
      zoom: base.zoom,
    };
    setCamera(target, { animate: true });
    return target;
  }

  return {
    getCamera,
    setCamera,
    zoomExtents,
    zoomAll,
    zoomWindow,
    zoomObject,
    zoomPrevious,
    pan,
    pushHistory,
  };
}
